import { useRef, useState } from "react";

import type {
  TeamSkillHistoryResult,
  TeamSkillOwnerAction,
  TeamSkillVersionComparisonResult,
} from "../../lib/account-client";

export function TeamSkillHistory({
  history,
  canPublish,
  canAdminister,
  pending,
  onPublish,
  onOwnerAction,
  onCompare,
  loadingMore,
  onLoadMore,
}: {
  history: TeamSkillHistoryResult;
  canPublish: boolean;
  canAdminister: boolean;
  pending: boolean;
  onPublish: (versionId: string) => void;
  onOwnerAction: (action: TeamSkillOwnerAction, versionId?: string) => void;
  onCompare: (versionId: string, baseVersionId?: string) => Promise<TeamSkillVersionComparisonResult>;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const { skill } = history;
  const [comparison, setComparison] = useState<TeamSkillVersionComparisonResult | null>(null);
  const [comparisonVersionId, setComparisonVersionId] = useState<string | null>(null);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const comparisonRequest = useRef(0);
  const capabilityByVersion = new Map(history.versions.map((version) => [version.versionId, version.capability]));
  const publishableVersion = skill.liveVersion ?? skill.latestSharedVersion;
  const newestVersionNumber = Math.max(...history.versions.map((version) => version.versionNumber), 0);
  const compare = async (versionId: string, baseVersionId?: string) => {
    if (comparisonVersionId === versionId) {
      comparisonRequest.current += 1;
      setComparisonVersionId(null);
      setLoadingComparison(false);
      return;
    }
    const request = ++comparisonRequest.current;
    setComparisonVersionId(versionId);
    setComparison(null);
    setComparisonError(null);
    setLoadingComparison(true);
    try {
      const next = await onCompare(versionId, baseVersionId);
      if (comparisonRequest.current === request) setComparison(next);
    } catch {
      if (comparisonRequest.current === request) setComparisonError("Doorway could not compare these versions. Try again.");
    } finally {
      if (comparisonRequest.current === request) setLoadingComparison(false);
    }
  };
  return (
    <section className="team-skill-history" aria-labelledby={`team-skill-${skill.skillId}-heading`}>
      <div className="team-skill-detail-heading">
        <div>
          <p className="step-label">Version history</p>
          <h3 id={`team-skill-${skill.skillId}-heading`}>{skill.displayName}</h3>
          <p>{skill.description || "No description yet."}</p>
        </div>
        <div className="team-skill-detail-actions">
          <span className={`team-skill-lifecycle team-skill-${skill.lifecycle}`}>{skill.lifecycle}</span>
          {canPublish && skill.lifecycle === "active" && publishableVersion && (
            <a className="secondary-button" href={publicLibraryPublisherUrl(history.workspaceId, skill, publishableVersion)}>
              Publish to public library
            </a>
          )}
        </div>
      </div>

      {skill.lifecycle === "disabled" && (
        <div className="team-skill-offline-boundary">
          <strong>Updates are stopped</strong>
          <span>Team members cannot download this skill. A copy already on a device may remain available until Doorway can reconnect and remove it.</span>
          {canAdminister && <button className="secondary-button" type="button" disabled={pending} onClick={() => onOwnerAction("re-enable")}>Re-enable</button>}
        </div>
      )}

      <ol className="team-skill-version-list">
        {history.versions.map((version) => {
          const baseCapability = version.baseVersionId ? capabilityByVersion.get(version.baseVersionId) : undefined;
          const capabilityChanges = summarizeCapabilityChanges(baseCapability, version.capability);
          const label = versionLabel(version, skill, newestVersionNumber);
          const comparisonOpen = comparisonVersionId === version.versionId;
          return (
            <li key={version.versionId}>
              <div className="team-skill-version-heading">
                <div>
                  <strong>Version {version.versionNumber}</strong>
                  <span className="team-skill-version-label">{label}</span>
                  {version.isLive && <span className="team-skill-live-badge">Team Live</span>}
                  <span className={`team-skill-risk team-skill-risk-${version.classification}`}>{version.classification === "privileged" ? "Declared access" : "Content only"}</span>
                </div>
                <time dateTime={version.createdAt}>{formatDate(version.createdAt)}</time>
              </div>
              <p className="team-skill-change-summary">{version.releaseNotes || version.changeSummary}</p>
              <dl className="team-skill-version-facts">
                <div><dt>Updated by</dt><dd>{version.contributor.name}</dd></div>
                <div><dt>Files</dt><dd>{version.fileCount}</dd></div>
                <div><dt>Capabilities</dt><dd>{capabilityChanges}</dd></div>
              </dl>
              <details className="team-skill-files">
                <summary>View files</summary>
                <ul>
                  {version.files.map((file) => (
                    <li key={file.ordinal}>
                      <span><bdi>{file.path}</bdi><small>{formatBytes(file.byteLength)} · mode {file.mode}</small></span>
                      {skill.lifecycle === "active"
                        ? <a href={fileDownloadUrl(history.workspaceId, skill.skillId, version.versionId, file.ordinal)}>Download</a>
                        : <small>Unavailable while {skill.lifecycle}</small>}
                    </li>
                  ))}
                </ul>
              </details>
              <div className="team-skill-version-comparison">
                <button
                  className="text-button"
                  type="button"
                  aria-expanded={comparisonOpen}
                  disabled={loadingComparison && comparisonOpen}
                  onClick={() => void compare(version.versionId, version.baseVersionId ?? undefined)}
                >
                  {comparisonOpen ? "Hide changes" : version.baseVersionId ? "See what changed" : "See what was added"}
                </button>
                {comparisonOpen && loadingComparison && <p role="status">Comparing versions…</p>}
                {comparisonOpen && comparisonError && <p className="error-copy" role="alert">{comparisonError}</p>}
                {comparisonOpen && comparison?.version.versionId === version.versionId && (
                  <VersionComparison comparison={comparison} />
                )}
              </div>
              <div className="team-skill-version-actions">
                {canPublish && !version.isLive && !version.hasBeenReleased && skill.lifecycle === "active" && !skill.locked && (
                  <button className="secondary-button" type="button" disabled={pending} onClick={() => onPublish(version.versionId)}>
                    Make Team Live
                  </button>
                )}
                {canAdminister && !version.isLive && version.hasBeenReleased && skill.lifecycle === "active" && (
                  <button className="text-button" type="button" disabled={pending} onClick={() => onOwnerAction("restore", version.versionId)}>Restore this version</button>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {history.events.length > 0 && (
        <details className="team-skill-audit">
          <summary>Activity history</summary>
          <ol>
            {history.events.map((event) => (
              <li key={event.releaseEventId}>
                <span>{event.actor.name} {eventLabel(event.reason)}</span>
                <time dateTime={event.createdAt}>{formatDate(event.createdAt)}</time>
              </li>
            ))}
          </ol>
        </details>
      )}
      {(history.pagination.versions.nextCursor !== null || history.pagination.events.nextCursor !== null) && (
        <button className="secondary-button" type="button" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? "Loading more…" : "Load more history"}
        </button>
      )}
    </section>
  );
}

function VersionComparison({ comparison }: { comparison: TeamSkillVersionComparisonResult }) {
  const changedCapabilities = capabilityDiffLabels(comparison.capabilityChanges);
  const { counts } = comparison.files;
  return (
    <div className="team-skill-comparison-result">
      <p className="team-skill-comparison-title">
        {comparison.baseVersion
          ? `Compared with version ${comparison.baseVersion.versionNumber}`
          : "First shared version"}
      </p>
      <p>
        {[counts.added && `${counts.added} added`, counts.updated && `${counts.updated} changed`, counts.removed && `${counts.removed} removed`]
          .filter(Boolean).join(", ") || "No file changes"}
      </p>
      <p>
        <strong>Access:</strong>{" "}
        {comparison.capabilityChanges === null
          ? "Initial access declaration"
          : comparison.capabilityChanges.changed
            ? changedCapabilities.join(", ")
            : "No access changes"}
      </p>
      {comparison.files.files.length > 0 && (
        <ul className="team-skill-diff-files">
          {comparison.files.files.map((file) => (
            <li key={file.path}>
              <div className="team-skill-diff-file-heading">
                <bdi>{file.path}</bdi>
                <span className={`team-skill-file-change team-skill-file-${file.kind}`}>{fileChangeLabel(file.kind)}</span>
              </div>
              {file.textDiff && (
                <pre className="team-skill-line-diff" aria-label={`Changes in ${file.path}`}>
                  {file.textDiff.lines.map((line, index) => (
                    <span className={`team-skill-diff-line team-skill-diff-${line.kind}`} key={`${index}-${line.kind}`}>
                      <span aria-hidden="true">{line.kind === "added" ? "+" : line.kind === "removed" ? "−" : " "}</span>
                      <code>{line.text || " "}</code>
                    </span>
                  ))}
                  {file.textDiff.truncated && <small>Preview shortened for readability.</small>}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
      {comparison.files.truncated && <small>Only the first 50 changed files are shown.</small>}
    </div>
  );
}

function versionLabel(
  version: TeamSkillHistoryResult["versions"][number],
  skill: TeamSkillHistoryResult["skill"],
  newestVersionNumber: number,
): string {
  if (version.isLive && version.releaseReason === "restore") return "Current · Restored";
  if (version.isLive) return "Current";
  if (skill.latestSharedVersion?.versionId === version.versionId || (!version.hasBeenReleased && version.versionNumber === newestVersionNumber)) {
    return "Pending";
  }
  if (skill.liveVersion && version.versionNumber === skill.liveVersion.versionNumber - 1) return "Previous";
  return version.hasBeenReleased ? "Earlier" : "Draft";
}

function capabilityDiffLabels(comparison: TeamSkillVersionComparisonResult["capabilityChanges"]): string[] {
  if (!comparison) return [];
  const added = Object.values(comparison.added).flat().length;
  const removed = Object.values(comparison.removed).flat().length;
  return [
    added > 0 && `${added} access declaration${added === 1 ? "" : "s"} added`,
    removed > 0 && `${removed} removed`,
    comparison.fromClassification !== comparison.toClassification
      && `changed from ${capabilityClassLabel(comparison.fromClassification)} to ${capabilityClassLabel(comparison.toClassification)}`,
  ].filter((value): value is string => Boolean(value));
}

function capabilityClassLabel(value: "content_only" | "privileged"): string {
  return value === "content_only" ? "content only" : "declared access";
}

function fileChangeLabel(kind: "added" | "updated" | "removed" | "unchanged"): string {
  return ({ added: "Added", updated: "Changed", removed: "Removed", unchanged: "Unchanged" })[kind];
}

function summarizeCapabilityChanges(before: Record<string, unknown> | undefined, after: Record<string, unknown>): string {
  if (!before) return capabilityCount(after) === 0 ? "No elevated access" : `${capabilityCount(after)} declared`;
  const oldValues = capabilityValues(before);
  const newValues = capabilityValues(after);
  const added = [...newValues].filter((value) => !oldValues.has(value)).length;
  const removed = [...oldValues].filter((value) => !newValues.has(value)).length;
  if (added === 0 && removed === 0) return "No change";
  return [added > 0 && `${added} added`, removed > 0 && `${removed} removed`].filter(Boolean).join(", ");
}

function capabilityCount(capability: Record<string, unknown>): number {
  return capabilityValues(capability).size;
}

function capabilityValues(capability: Record<string, unknown>): Set<string> {
  const values = new Set<string>();
  for (const [key, entries] of Object.entries(capability)) {
    if (!Array.isArray(entries)) continue;
    for (const entry of entries) if (typeof entry === "string") values.add(`${key}:${entry}`);
  }
  return values;
}

function fileDownloadUrl(workspaceId: string, skillId: string, versionId: string, ordinal: number): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/files/${ordinal}:download`;
}

function publicLibraryPublisherUrl(
  workspaceId: string,
  skill: TeamSkillHistoryResult["skill"],
  version: { versionId: string; versionNumber: number },
): string {
  const query = new URLSearchParams({
    name: skill.displayName,
    slug: skill.canonicalName,
    summary: skill.description,
    workspaceId,
    skillId: skill.skillId,
    versionId: version.versionId,
    versionNumber: String(version.versionNumber),
  });
  if (skill.tags?.length) query.set("tags", skill.tags.join(", "));
  return `/manage/library?${query.toString()}`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  return `${(value / 1024).toFixed(value < 10 * 1024 ? 1 : 0)} KB`;
}

function eventLabel(reason: string): string {
  return ({ publish: "published a version", approve: "approved a version", restore: "restored a version", lock: "locked updates", unlock: "unlocked updates", disable: "disabled the skill", "re-enable": "re-enabled the skill", remove: "removed the skill" } as Record<string, string>)[reason] ?? reason;
}
